import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import IssuerLayout from "@/components/IssuerLayout";
import CertificateEditor from "@/components/CertificateEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateCertificate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [templateLayout, setTemplateLayout] = useState(null);
  const [templateModified, setTemplateModified] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [pendingTemplateData, setPendingTemplateData] = useState(null);
  const [formData, setFormData] = useState({
    title: "Certificate of Achievement",
    course_name: "",
    template_id: "",
    completion_date: new Date().toISOString().split('T')[0],
    instructor_name: "",
    title_font_style: "serif",
    logo_base64: "",
    expiration_date: ""
  });

  const fontStyles = [
    { id: "serif", name: "Classic Serif", font: "Playfair Display, serif" },
    { id: "sans", name: "Modern Sans", font: "Manrope, sans-serif" },
    { id: "display", name: "Bold Display", font: "'Arial Black', sans-serif" },
    { id: "elegant", name: "Elegant Script", font: "'Georgia', serif" }
  ];

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle paginated response
      const templateData = response.data.items || response.data;
      setTemplates(templateData);
      if (templateData.length > 0) {
        setFormData(prev => ({ ...prev, template_id: templateData[0].id }));
      }
    } catch (err) {
      toast.error("Failed to load templates");
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      const MAX_SIZE = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > MAX_SIZE) {
        toast.error("Logo must be less than 2MB. Please choose a smaller file.");
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file (PNG, JPG, etc.)");
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData(prev => ({ ...prev, logo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveTemplate = (templateData) => {
    const selectedTemplate = templates.find(t => t.id === formData.template_id);
    
    // If modifying a system template AND user actually modified it, prompt to save as new custom template
    if (selectedTemplate?.is_system && templateModified) {
      setPendingTemplateData(templateData);
      setShowSaveTemplateDialog(true);
    } else {
      // Save layout for custom template or just store for certificate creation
      setTemplateLayout(templateData);
      setTemplateModified(false);
      toast.success("Template layout saved!");
    }
  };

  const handleSaveCustomTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    const selectedTemplate = templates.find(t => t.id === formData.template_id);
    
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.post(`${API}/templates`, {
        name: newTemplateName,
        type: selectedTemplate.type,
        background_url: selectedTemplate.background_url,
        layout: pendingTemplateData,
        is_system: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Custom template "${newTemplateName}" saved!`);
      // Refresh templates and select the new one
      await fetchTemplates();
      setFormData(prev => ({ ...prev, template_id: response.data.id }));
      setTemplateLayout(pendingTemplateData);
      setTemplateModified(false);
      setShowSaveTemplateDialog(false);
      setNewTemplateName("");
      setPendingTemplateData(null);
    } catch (err) {
      toast.error("Failed to save custom template");
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If user modified the template, they must save it
    // If user didn't modify, we can use the template's original layout
    if (templateModified) {
      toast.error("Please save your template changes using the 'Save Template' button before creating the certificate");
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('issuer_token');
      
      // Use saved layout if available, otherwise fetch the template's layout
      let layoutToUse = templateLayout;
      if (!layoutToUse) {
        const selectedTemplate = templates.find(t => t.id === formData.template_id);
        layoutToUse = selectedTemplate?.layout || null;
      }
      
      const response = await axios.post(`${API}/certificates`, {
        title: formData.title,
        course_name: formData.course_name,
        template_id: formData.template_id,
        expiration_date: formData.expiration_date,
        template_layout: layoutToUse,
        instructor_name: formData.instructor_name,
        logo_base64: formData.logo_base64
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Certificate created! Now add recipients.");
      navigate(`/issuer/issue/${response.data.id}`);
    } catch (err) {
      console.error("Certificate creation error:", err);
      const errorMsg = err.response?.data?.detail 
        ? (typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail))
        : "Failed to create certificate";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const selectedFont = fontStyles.find(f => f.id === formData.title_font_style);
  const selectedTemplate = templates.find(t => t.id === formData.template_id);

  return (
    <IssuerLayout>
      <div data-testid="create-certificate-page">
        <div className="mb-8">
          <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Create Certificate</h1>
          <p className="text-slate-600">Design a new certificate template</p>
        </div>

        <div className="space-y-8">
          {/* Left Side - Form */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-certificate-form">
              <div>
                <Label htmlFor="title" className="text-sm font-medium text-slate-700 mb-1.5 block">Certificate Title</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Certificate of Achievement"
                  required
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="course-name" className="text-sm font-medium text-slate-700 mb-1.5 block">Course Name</Label>
                <Input
                  id="course-name"
                  type="text"
                  value={formData.course_name}
                  onChange={(e) => setFormData({...formData, course_name: e.target.value})}
                  placeholder="e.g., Advanced Web Development"
                  required
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="course-name-input"
                />
              </div>

              <div>
                <Label htmlFor="title-font" className="text-sm font-medium text-slate-700 mb-1.5 block">Title Font Style</Label>
                <Select value={formData.title_font_style} onValueChange={(value) => setFormData({...formData, title_font_style: value})}>
                  <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="title-font-select">
                    <SelectValue placeholder="Select font style" />
                  </SelectTrigger>
                  <SelectContent>
                    {fontStyles.map(style => (
                      <SelectItem key={style.id} value={style.id} data-testid={`font-option-${style.id}`}>
                        <span style={{fontFamily: style.font}}>{style.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="template" className="text-sm font-medium text-slate-700 mb-1.5 block">Template</Label>
                <Select 
                  value={formData.template_id} 
                  onValueChange={(value) => {
                    setFormData({...formData, template_id: value});
                    // Clear saved layout when changing templates - user must explicitly save
                    setTemplateLayout(null);
                    setTemplateModified(false);
                  }} 
                  required
                >
                  <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="template-select">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id} data-testid={`template-option-${template.id}`}>
                        {template.name} ({template.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="instructor" className="text-sm font-medium text-slate-700 mb-1.5 block">Instructor's Name</Label>
                <Input
                  id="instructor"
                  type="text"
                  value={formData.instructor_name}
                  onChange={(e) => setFormData({...formData, instructor_name: e.target.value})}
                  placeholder="e.g., Dr. Jane Smith"
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="instructor-input"
                />
              </div>

              <div>
                <Label htmlFor="completion-date" className="text-sm font-medium text-slate-700 mb-1.5 block">Default Completion Date</Label>
                <Input
                  id="completion-date"
                  type="date"
                  value={formData.completion_date}
                  onChange={(e) => setFormData({...formData, completion_date: e.target.value})}
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="completion-date-input"
                />
              </div>

              <div>
                <Label htmlFor="logo" className="text-sm font-medium text-slate-700 mb-1.5 block">Logo / Image Upload</Label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-input"
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-colors">
                      <Upload className="h-4 w-4 text-slate-600" />
                      <span className="text-sm text-slate-700">Choose File</span>
                    </div>
                  </label>
                  {logoPreview && (
                    <div className="h-12 w-12 border border-slate-200 rounded-lg overflow-hidden">
                      <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="expiration" className="text-sm font-medium text-slate-700 mb-1.5 block">Expiration Date (Optional)</Label>
                <Input
                  id="expiration"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({...formData, expiration_date: e.target.value})}
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="expiration-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3 font-medium"
                  data-testid="create-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    "Create & Issue Certificates"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/issuer/dashboard")}
                  className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-6 py-3"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>

          {/* Certificate Editor */}
          {selectedTemplate && (
            <CertificateEditor
              key={formData.template_id}
              templateBackground={selectedTemplate?.background_url ? `${API}/proxy-image?url=${encodeURIComponent(selectedTemplate.background_url)}` : null}
              onSave={handleSaveTemplate}
              onModify={() => setTemplateModified(true)}
              logoPreview={logoPreview}
              instructorName={formData.instructor_name}
              courseName={formData.course_name}
              initialLayout={selectedTemplate?.layout}
              isCustomBackground={true}
            />
          )}
        </div>
      </div>

      {/* Save Custom Template Dialog */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as Custom Template</DialogTitle>
            <DialogDescription>
              You've modified a system template. Save your changes as a new custom template to reuse for other courses.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="template-name" className="text-sm font-medium text-slate-700 mb-2 block">
              Template Name
            </Label>
            <Input
              id="template-name"
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="e.g., My Custom Blue Certificate"
              className="h-11 rounded-lg border-slate-200"
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowSaveTemplateDialog(false);
                setNewTemplateName("");
                setPendingTemplateData(null);
                // Still save the layout for this certificate
                setTemplateLayout(pendingTemplateData);
                setTemplateModified(false);
                toast.success("Template layout saved for this certificate only");
              }}
            >
              Skip & Use Once
            </Button>
            <Button
              type="button"
              onClick={handleSaveCustomTemplate}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </IssuerLayout>
  );
};

export default CreateCertificate;
